import type { ReactNode } from 'react'
import { Box, Flex, Portal, Text, Tooltip } from '@minetrace/ui'
import { m3, themeColors } from '../../../theme'

/**
 * Tooltip de la vista archivística. El contenido de estos tooltips no es decorativo:
 * es donde se declara que una entrada digitalizada no pasó por fedatario o que una
 * serie está sin clasificar, así que se monta con `Portal` para que nunca lo recorte
 * el `overflow` de la tabla.
 */
export function Hint({
  label,
  children,
  maxW = '320px',
}: {
  label: ReactNode
  children: ReactNode
  maxW?: string
}) {
  return (
    <Tooltip.Root openDelay={120} closeDelay={80}>
      <Tooltip.Trigger asChild>
        <Box display="inline-flex" alignItems="center" cursor="help">
          {children}
        </Box>
      </Tooltip.Trigger>
      <Portal>
        <Tooltip.Positioner>
          <Tooltip.Content
            bg={m3.inverseSurface}
            color={m3.inverseOnSurface}
            px={3}
            py={2}
            borderRadius="md"
            fontSize="xs"
            lineHeight="1.5"
            maxW={maxW}
            zIndex={2000}
          >
            {label}
          </Tooltip.Content>
        </Tooltip.Positioner>
      </Portal>
    </Tooltip.Root>
  )
}

/** Chip compacto de estado. `tone` decide el color; nunca lo decide el llamador a ojo. */
export function Chip({
  icon,
  children,
  color,
  bg,
  mono,
}: {
  icon?: ReactNode
  children: ReactNode
  color: string
  bg: string
  mono?: boolean
}) {
  return (
    <Flex
      alignItems="center"
      gap={1.5}
      px={2.5}
      py={1}
      borderRadius="full"
      bg={bg}
      color={color}
      flexShrink={0}
    >
      {icon}
      <Text fontSize="11px" fontWeight="700" fontFamily={mono ? 'monospace' : undefined} lineHeight="1.4">
        {children}
      </Text>
    </Flex>
  )
}

/** Par etiqueta/valor de las fichas de metadatos. */
export function Field({
  label,
  value,
  mono,
  hint,
}: {
  label: string
  value: ReactNode
  mono?: boolean
  hint?: ReactNode
}) {
  return (
    <Box minW={0}>
      <Flex alignItems="center" gap={1}>
        <Text
          fontSize="10px"
          fontWeight="700"
          color={themeColors.textMuted}
          textTransform="uppercase"
          letterSpacing="0.05em"
        >
          {label}
        </Text>
        {hint}
      </Flex>
      <Text
        fontSize="sm"
        color={themeColors.text}
        fontFamily={mono ? 'monospace' : undefined}
        wordBreak={mono ? 'break-all' : undefined}
      >
        {value}
      </Text>
    </Box>
  )
}
